const BASE = "https://nigeria.jamiuadewaleyusuf.com/api/v1";

const stateCache = new Map(); // slug -> { coordinates, lgas } | null
const routeCache = new Map();

function toSlug(s) {
  return s
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

async function getStateWithLgas(state) {
  const slug = toSlug(state);
  if (!slug) return null;
  if (stateCache.has(slug)) return stateCache.get(slug);

  try {
    const [stateRes, lgaRes] = await Promise.all([
      fetch(`${BASE}/states/${slug}`),
      fetch(`${BASE}/states/${slug}/lgas`),
    ]);

    if (!stateRes.ok) {
      console.log("[getStateWithLgas] state lookup failed", slug, stateRes.status);
      stateCache.set(slug, null);
      return null;
    }

    const stateJson = await stateRes.json();
    if (stateJson.status !== "success" || !stateJson.data) {
      console.log("[getStateWithLgas] bad state response", slug, stateJson);
      stateCache.set(slug, null);
      return null;
    }

    const lgaJson = lgaRes.ok ? await lgaRes.json() : null;

    const result = {
      coordinates: stateJson.data.coordinates ?? null,
      lgas: lgaJson?.status === "success" ? lgaJson.data ?? [] : [],
    };

    console.log(`[getStateWithLgas] loaded "${slug}":`, result.coordinates, `${result.lgas.length} LGAs`);
    stateCache.set(slug, result);
    return result;
  } catch (e) {
    console.log("[getStateWithLgas] threw", e);
    stateCache.set(slug, null);
    return null;
  }
}

/**
 * Resolves a job's location to coordinates using structured state/LGA
 * data (not free-text search, which was matching random street names).
 * Falls back to the state's center coordinates if the LGA isn't found.
 */
export async function geocodeAddress(state, localGovernment) {
  if (!state) return null;

  const stateData = await getStateWithLgas(state);
  if (!stateData) {
    console.log(`[geocodeAddress] unknown state "${state}"`);
    return null;
  }

  if (localGovernment) {
    const lgaSlug = toSlug(localGovernment);
    const match = stateData.lgas.find(
      (l) => l.slug === lgaSlug || toSlug(l.name) === lgaSlug
    );
    if (match?.coordinates) {
      console.log(`[geocodeAddress] matched LGA "${localGovernment}" in "${state}"`);
      return match.coordinates;
    }
    console.log(
      `[geocodeAddress] LGA "${localGovernment}" not found in "${state}", falling back to state center`
    );
  }

  return stateData.coordinates ?? null;
}

/**
 * Road route between two coordinate pairs. Returns
 * { distanceMeters, durationSeconds, distanceText, durationText } or null.
 */
export async function getDrivingRoute(from, to) {
  if (!from || !to) return null;
  const key = `${from.lat},${from.lng}-${to.lat},${to.lng}`;
  if (routeCache.has(key)) return routeCache.get(key);

  try {
    const params = new URLSearchParams({
      from_lat: from.lat,
      from_lng: from.lng,
      to_lat: to.lat,
      to_lng: to.lng,
    });

    const res = await fetch(`${BASE}/route?${params.toString()}`);
    if (!res.ok) {
      console.log("[getDrivingRoute] HTTP error", res.status, await res.text());
      routeCache.set(key, null);
      return null;
    }

    const json = await res.json();
    if (json.status !== "success" || !json.data) {
      console.log("[getDrivingRoute] rejected", json);
      routeCache.set(key, null);
      return null;
    }

    const result = {
      distanceMeters: json.data.distance_km != null ? json.data.distance_km * 1000 : null,
      durationSeconds: json.data.duration_minutes != null ? json.data.duration_minutes * 60 : null,
      distanceText: json.data.distance_text ?? null,
      durationText: json.data.duration_text ?? null,
    };

    routeCache.set(key, result);
    return result;
  } catch (e) {
    console.log("[getDrivingRoute] threw", e);
    routeCache.set(key, null);
    return null;
  }
}